// App.jsx
import { useEffect, useState } from 'react';
import api from './api';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
 
import NewExpenseForm from './pages/NewExpenseForm';
import ExpenseReport from './pages/ExpenseReport';
import ExpenseList from './pages/ExpenseList';
import Login from './pages/Login';
import ExpenseDetails from './pages/ExpenseDetails';
import ApprovalPage from './pages/ApprovalPage';
import Approvals from './pages/Approvals';
import Dashboard from './pages/Dashboard';
import ProceedToPayment from './pages/ProceedToPayment';
import PendingApprovalList from './pages/PendingApprovalList';
import ApprovalsList from './pages/ApprovalList';
import SidebarLayout from './pages/SidebarLayout';
import { getAccessToken } from './utils/getAccessToken';
import PaymentAdjustment from './pages/PaymentAdjustment'; 
import SentToPaymentList from "./pages/SentToPaymentList";
import FeedbackPage from './pages/FeedbackPage';
import L3ApprovalReport from "./pages/L3ApprovalReport";
import PaymentManagement from "./pages/PaymentManagement";
import InventoryDashboard from './pages/InventoryDashboard';
import NonInventoryDashboard from './pages/NonInventoryDashboard';
import DebugDashboard from './pages/DebugDashboard';
import BudgetDashboard from './pages/BudgetDashboard';
import GoodsReceipt from './pages/GoodsReceipt';
import GoodsReceiptsPage from './pages/GoodsReceiptsPage';
import ExpenseItems from './pages/ExpenseItems';
import FAQPage from './pages/FAQPage';
import ProductionUrlSwitcher from './components/ProductionUrlSwitcher';
import NetworkDiagnostic from './components/NetworkDiagnostic';

function App() {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const activeAccount = instance?.getActiveAccount && instance.getActiveAccount();
  const userEmail = activeAccount?.username || (isAuthenticated && accounts.length > 0 ? accounts[0].username : null);
 
  const [userRole, setUserRole] = useState(() => {
    // Check localStorage first, then default to admin for known admin users
    const savedRole = localStorage.getItem('userRole');
    return savedRole || 'admin';
  });

  // 🔁 Fetch role from backend (original behavior)
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        if (!userEmail) {
          console.debug('No user email yet; waiting for MSAL accounts…');
          return;
        }
        
        // Check if this is a known admin user first
        const adminEmails = [
          'tanvi.laddha@aucglobal.com',
          // Add other admin emails here
        ];
        
        if (userEmail && adminEmails.includes(userEmail.toLowerCase())) {
          setUserRole('admin');
          localStorage.setItem('userRole', 'admin');
          return; // Don't fetch from backend, keep admin role
        }

        const res = await api.get(`/api/users/${userEmail}`);
        setUserRole(res.data.role);
        localStorage.setItem('userRole', res.data.role);
      } catch (err) {
        console.error("❌ Error fetching user role:", {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status
        });
        
        // Enhanced fallback logic for admin users
        const adminEmails = [
          'tanvi.laddha@aucglobal.com',
          // Add other admin emails here
        ];
        
        if (userEmail && adminEmails.includes(userEmail.toLowerCase())) {
          setUserRole('admin');
          localStorage.setItem('userRole', 'admin');
        } else {
          // Fallback so UI still renders sidebar/menu for basic users
          setUserRole('user');
          localStorage.setItem('userRole', 'user');
        }
      }
    };

    fetchUserRole();
  }, [userEmail]);

  // 🔁 Sync user to backend DB (original behavior)
  useEffect(() => {
    const syncUser = async () => {
      try {
        const account = accounts[0];
        if (!account) return;

        const response = await instance.acquireTokenSilent({
          scopes: ['User.Read'],
          account,
        });

        const token = response.accessToken;

        await api.post('/api/users/sync', {}, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      } catch (error) {
        console.error('❌ Error syncing user:', error);
      }
    };

    syncUser();
  }, [instance, accounts]);
 
  // Redirect handling is centralized in main.jsx
 
  // Do not block rendering; SidebarLayout has its own fallback nav for unknown roles
  
  const userRoles = typeof userRole === 'string' ? userRole.split(',').map(r => r.trim()) : [userRole || 'creator'];
  const hasRole = (role) => userRoles.includes(role);
 
  return (
    <BrowserRouter>
      <Routes>
        {isAuthenticated ? (
          <Route path="/" element={<SidebarLayout userEmail={userEmail} userRole={userRole} />}>
            <Route index element={<Navigate to="/inventory-dashboard" replace />} />

            {/* 🔐 Creator/Admin-only routes (create/edit) */}
            {(hasRole('admin') || hasRole('creator')) && (
              <>
                <Route path="new-expense" element={<NewExpenseForm />} />
                <Route path="expenses" element={<ExpenseList userRole={userRole} />} />
                <Route path="expense-details" element={<ExpenseDetails />} />
                <Route path="expense-details/:expenseId" element={<ExpenseDetails />} />
                <Route path="payment/:expenseId" element={<ProceedToPayment />} />

                {/* Payment Adjustment and Sent To Payment are defined below with role-based handling */}
              </>
            )}

            {/* 🔐 Approver, Admin, Creator & Payment (view-only where enforced by component) */}
            {(hasRole('approver') || hasRole('admin') || hasRole('creator') || hasRole('payment')) && (
              <>
                {/* approve/:expenseId is now available for all below in Common section */}
                <Route path="expenses" element={<ExpenseList userRole={userRole} />} />
                <Route path="approve" element={<Approvals userEmail={userEmail} userRole={userRole} />} />
                <Route path="sent-to-payment" element={<SentToPaymentList userRole={userRole} />} />
                <Route path="l3-approval/:expenseId/:vendorId?" element={<L3ApprovalReport />} />
              </>
            )}

            {/* 🔐 Dashboards moved to common section for deep linking */}
            
            {/* Debug route - remove in production */}
            <Route path="debug-dashboard" element={<DebugDashboard />} />
            
            {/* Payment Management route is always available post-login; component enforces role-based behavior */}
            <Route path="payment-management" element={<PaymentManagement />} />

            {/* 🟡 Common for all (deep linkable irrespective of role timing) */}
            {/* Dashboard routes - always available, components handle role-based access */}
            <Route path="inventory-dashboard" element={<InventoryDashboard />} />
            <Route path="budget-dashboard" element={<BudgetDashboard />} />
            
            {/* Make deep linkable approval page available irrespective of role load timing */}
            <Route path="approve/*" element={<ApprovalPage userRole={userRole} />} />
            {/* Allow route to match even if userRole hasn't loaded yet; component can enforce role if needed */}
            <Route path="expense-report/*" element={<ExpenseReport />} />
            <Route path="expense-items/:expenseId" element={<ExpenseItems />} />
            <Route path="pending-approval-list" element={<PendingApprovalList />} />
            <Route path="approved-list" element={<ApprovalsList />} />
            {/* Goods Receipt: admin/creator can post and view receipts; payment and approver can view-only */}
            {(hasRole('admin') || hasRole('creator') || hasRole('payment') || hasRole('approver')) && (
              <>
                <Route path="goods-receipts" element={<GoodsReceiptsPage />} />
                {(hasRole('admin') || hasRole('creator')) && (
                  <>
                    <Route path="expenses/:expenseId/receipts" element={<GoodsReceipt />} />
                    <Route path="goods-receipt/:expenseId" element={<GoodsReceipt />} />
                  </>
                )}
              </>
            )}
            {/* Feedback view: admin/creator can submit/update, others (approver/payment) view-only */}
            <Route
              path="feedback"
              element={<FeedbackPage readOnly={!(hasRole('admin') || hasRole('creator'))} />}
            />
            <Route path="faq" element={<FAQPage />} />
            
            {/* 🔧 Debug/Admin Tools */}
            {hasRole('admin') && (
              <>
                <Route path="debug-url" element={<ProductionUrlSwitcher />} />
                <Route path="debug-network" element={<NetworkDiagnostic />} />
              </>
            )}

            {/* Payment Adjustment: allow payment/approver view-only by passing role; admin/creator full */}
            {(hasRole('admin') || hasRole('creator') || hasRole('payment') || hasRole('approver')) && (
              <>
                <Route path="payment-adjustment/:expenseId" element={<PaymentAdjustment userRole={userRole} />} />
                <Route path="payment-adjustment/:expenseId/:vendorId" element={<PaymentAdjustment userRole={userRole} />} />
              </>
            )}
          </Route>

        ) : (
          <Route path="*" element={<Login />} />
        )}
      </Routes>
    </BrowserRouter>
  );
}
 
export default App;




// // App.jsx
// import { useEffect } from 'react';
// import { BrowserRouter, Routes, Route } from 'react-router-dom';
// import { useIsAuthenticated, useMsal } from '@azure/msal-react';
// import NewExpenseForm from './pages/NewExpenseForm';
// import ExpenseReport from './pages/ExpenseReport';
// import ExpenseList from './pages/ExpenseList';
// import Login from './pages/Login';
// import ExpenseDetails from './pages/ExpenseDetails';
// import ApprovalPage from './pages/ApprovalPage';
// import Approvals from './pages/Approvals';
// import Dashboard from './pages/Dashboard';
// import ProceedToPayment from './pages/ProceedToPayment';
// import PendingApprovalList from './pages/PendingApprovalList';
// import ApprovalsList from './pages/ApprovalList';
// import SidebarLayout from './pages/SidebarLayout';

// function App() {
//   const { instance, accounts } = useMsal();
//   const isAuthenticated = useIsAuthenticated();
//   const userEmail = isAuthenticated && accounts.length > 0 ? accounts[0].username : null;

//   useEffect(() => {
//     instance.handleRedirectPromise().catch((error) => {
//       console.error("Redirect error:", error);
//     });
//   }, [instance]);

//   return (
//     <BrowserRouter>
//       <Routes>
//         {isAuthenticated ? (
//           <Route path="/" element={<SidebarLayout />}>
//             <Route index element={<Dashboard />} />
//             <Route path="new-expense" element={<NewExpenseForm />} />
//             <Route path="expenses" element={<ExpenseList />} />
//             <Route path="expense-details" element={<ExpenseDetails />} />
//             <Route path="expense-details/:expenseId" element={<ExpenseDetails />} />
//             <Route path="expense-report/:expenseId" element={<ExpenseReport />} />
//             <Route path="approve/:expenseId" element={<ApprovalPage />} />
//             <Route path="approve" element={<Approvals userEmail={userEmail} />} />
//             <Route path="payment/:expenseId" element={<ProceedToPayment />} />
//             <Route path="pending-approval-list" element={<PendingApprovalList />} />
//             <Route path="approved-list" element={<ApprovalsList />} />
//           </Route>
//         ) : (
//           <Route path="*" element={<Login />} />
//         )}
//       </Routes>
//     </BrowserRouter>
//   );
// }

// export default App;

